import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { Lock, Mail, ArrowLeft, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import '@/index.admin.css';
import AdminPwaHead from '@/components/admin/AdminPwaHead';
import AdminAmbientShell from '@/components/admin/AdminAmbientShell';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function AdminLogin() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const reduceMotion = useReducedMotion();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/admin/login`, formData);
      
      if (response.data.token) {
        login(response.data.token, response.data.user);
        toast.success('Login successful!');
        const sections = response.data.user?.sections || [];
        const isBootstrap = response.data.user?.is_bootstrap_admin;
        const order = [
          ['dashboard', '/admin'],
          ['inventory', '/admin/inventory'],
          ['deals', '/admin/deals'],
          ['documents', '/admin/documents'],
          ['operations', '/admin/operations'],
          ['leads', '/admin/leads'],
          ['service', '/admin/service-requests'],
          ['outreach', '/admin/outreach'],
          ['schedule', '/admin/schedule'],
          ['user_management', '/admin/users'],
        ];
        let path = '/admin';
        for (const [sec, p] of order) {
          if (isBootstrap || sections.includes(sec)) {
            path = p;
            break;
          }
        }
        navigate(path);
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Invalid email or password';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <AdminAmbientShell className="items-center justify-center px-4">
      <AdminPwaHead />
      <button
        onClick={() => navigate('/')}
        className="absolute z-20 top-[max(1.5rem,env(safe-area-inset-top))] left-[max(1rem,env(safe-area-inset-left))] min-h-11 text-white/60 hover:text-white transition-colors flex items-center gap-2 px-2"
      >
        <ArrowLeft className="w-5 h-5" />
        <span className="font-['Manrope']">Back to Home</span>
      </button>

      {/* Login Card */}
      <motion.div
        initial={reduceMotion ? false : { opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: reduceMotion ? 0 : 0.35 }}
        className="relative z-10 w-full max-w-md"
      >
        <div className="admin-glass-elevated p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-500/10 border border-blue-500/20 mb-4">
              <Lock className="w-8 h-8 text-blue-400" />
            </div>
            <h1 className="text-3xl font-['Unbounded'] font-bold text-white mb-2">
              Admin Login
            </h1>
            <p className="text-white/60 font-['Manrope']">
              Access the dealership dashboard
            </p>
            <p className="text-white/40 font-['Manrope'] text-xs mt-3">
              After you sign in, you can install the staff app on this phone.
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email Input */}
            <div>
              <label htmlFor="email" className="block text-sm font-['Manrope'] font-medium text-white/80 mb-2">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full bg-white/5 border border-white/10 text-white pl-11 pr-4 py-3 font-['Manrope'] focus:outline-none focus:border-blue-500/50 focus:ring-1 focus:ring-blue-500/50 transition-all"
                  placeholder="Email address"
                />
              </div>
            </div>

            {/* Password Input */}
            <div>
              <label htmlFor="password" className="block text-sm font-['Manrope'] font-medium text-white/80 mb-2">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  className="w-full bg-white/5 border border-white/10 text-white pl-11 pr-12 py-3 font-['Manrope'] focus:outline-none focus:border-blue-500/50 focus:ring-1 focus:ring-blue-500/50 transition-all"
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/80 transition-colors inline-flex items-center justify-center min-h-11 min-w-11"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  data-testid="toggle-password-visibility"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-blue-500 hover:bg-blue-600 text-white font-['Manrope'] font-semibold py-3 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Signing In...' : 'Sign In'}
            </button>
          </form>
        </div>
      </motion.div>
    </AdminAmbientShell>
  );
}
